'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSavedVehicles } from '@/hooks/useSavedVehicles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEye, faArrowLeft, faCar } from '@fortawesome/free-solid-svg-icons';
import { Heart, Phone } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { sendGTMEvent } from '@next/third-parties/google';
import { toGAItem, createPhoneClickEventData, type VehicleData } from '@/utils/ga4-events';
import type { SavedVehicle } from '@/services/savedVehiclesService';
import { usePricing } from '@/components/pricing/PricingProvider';
import { formatCurrency } from '@/utils/pricing';
import { refreshSavedVehicle, type RefreshedSavedVehicle } from '@/services/savedVehicleRefresh';
export default function FavoritesPage() {
  const $ = _c(38);
  const {
    savedVehicles,
    unsaveVehicle,
    clearAll,
    isLoading
  } = useSavedVehicles();
  const {
    getNoHagglePrice
  } = usePricing();
  const [removingVin, setRemovingVin] = useState(null);
  const apiUrl = process.env.NEXT_PUBLIC_INVENTORY_API_URL;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [currentVehicles, setCurrentVehicles] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== isLoading || $[2] !== savedVehicles) {
    t1 = () => {
      if (!apiUrl || isLoading) {
        return;
      }
      let cancelled = false;
      let running = false;
      const refresh = async () => {
        if (running) {
          return;
        }
        running = true;
        for (let start = 0; start < savedVehicles.length && !cancelled; start = start + 4, start) {
          const entries = await Promise.all(savedVehicles.slice(start, start + 4).map(_temp));
          if (!cancelled) {
            setCurrentVehicles(previous => ({
              ...previous,
              ...Object.fromEntries(entries)
            }));
          }
        }
        running = false;
      };
      refresh();
      window.addEventListener("focus", refresh);
      const timer = window.setInterval(refresh, 120000);
      return () => {
        cancelled = true;
        window.removeEventListener("focus", refresh);
        window.clearInterval(timer);
      };
    };
    t2 = [apiUrl, isLoading, savedVehicles];
    $[1] = isLoading;
    $[2] = savedVehicles;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[5] !== unsaveVehicle) {
    t3 = async vin => {
      setRemovingVin(vin);
      sendGTMEvent({
        event: "remove_from_wishlist",
        item_id: vin,
        event_category: "favorites",
        event_label: "individual_remove"
      });
      await unsaveVehicle(vin);
      setRemovingVin(null);
    };
    $[5] = unsaveVehicle;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleRemove = t3;
  let t4;
  if ($[7] !== getNoHagglePrice) {
    t4 = price => formatCurrency(getNoHagglePrice(price));
    $[7] = getNoHagglePrice;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const formatPrice = t4;
  const formatMileage = _temp2;
  let t5;
  let t6;
  if ($[9] !== isLoading || $[10] !== savedVehicles) {
    t5 = () => {
      if (isLoading) {
        return;
      }
      ;
      try {
        const items = savedVehicles.slice(0, 50).map(_temp3);
        if (items.length > 0) {
          sendGTMEvent({
            event: "view_item_list",
            item_list_name: "favorites",
            items,
            list_page: 1,
            list_sort: "favorites"
          });
        }
      } catch (t7) {
        const e = t7;
        console.warn("Favorites: failed to send view_item_list", e);
      }
    };
    t6 = [isLoading, savedVehicles];
    $[9] = isLoading;
    $[10] = savedVehicles;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  React.useEffect(t5, t6);
  if (isLoading) {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="t-h-8 t-bg-ecommerce-background-alt t-rounded t-w-48 t-mb-8" />;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="t-min-h-screen t-bg-ecommerce-background t-py-12"><div className="t-max-w-screen-2xl t-mx-auto t-px-4"><div className="t-animate-pulse">{t7}<div className="t-grid t-grid-cols-1 md:t-grid-cols-2 lg:t-grid-cols-3 xl:t-grid-cols-4 t-gap-6">{[...Array(4)].map(_temp4)}</div></div></div></div>;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Link href="/cars" onClick={_temp5} className="t-inline-flex t-items-center t-space-x-2 t-text-automotive-primary hover:t-text-automotive-secondary t-transition-colors t-mb-4"><FontAwesomeIcon icon={faArrowLeft} className="t-text-sm" /><span>Back to Inventory</span></Link>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h1 className="t-text-3xl t-font-bold t-text-automotive-dark">Favorite Vehicles</h1>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = savedVehicles.length !== 1 ? "s" : "";
  let t10;
  if ($[17] !== savedVehicles.length || $[18] !== t9) {
    t10 = <div>{t8}<p className="t-text-ecommerce-text-light t-mt-2">{savedVehicles.length} vehicle{t9} in favorites</p></div>;
    $[17] = savedVehicles.length;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== clearAll || $[21] !== savedVehicles.length) {
    t11 = savedVehicles.length > 0 && <button onClick={() => {
      if (confirm("Are you sure you want to clear all favorite vehicles?")) {
        sendGTMEvent({
          event: "remove_from_wishlist",
          event_category: "favorites",
          event_label: "clear_all",
          value: savedVehicles.length
        });
        clearAll();
      }
    }} className="t-text-sm t-text-gray-500 hover:t-text-white hover:t-bg-brand-rose t-px-3 t-py-1.5 t-rounded-lg t-border t-border-gray-300 hover:t-border-brand-rose t-transition-all t-duration-200 hover:t-shadow-md focus:t-ring-2 focus:t-ring-brand-rose/20 focus:t-outline-none">Clear All</button>;
    $[20] = clearAll;
    $[21] = savedVehicles.length;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div className="t-mb-8">{t7}<div className="t-flex t-items-center t-justify-between">{t10}{t11}</div></div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== currentVehicles || $[27] !== formatPrice || $[28] !== handleRemove || $[29] !== removingVin || $[30] !== savedVehicles) {
    t13 = savedVehicles.length === 0 ? <div className="t-bg-white t-rounded-lg t-shadow-card t-p-12 t-text-center"><div className="t-text-6xl t-text-ecommerce-text-muted t-mb-4"><FontAwesomeIcon icon={faCar} /></div><h2 className="t-text-2xl t-font-semibold t-text-automotive-dark t-mb-2">No favorite vehicles yet</h2><p className="t-text-ecommerce-text-light t-mb-6">Start browsing our inventory and add vehicles to your favorites for quick access later.</p><Link href="/cars" onClick={_temp6} className="t-inline-flex t-items-center t-justify-center t-gap-3 t-bg-gradient-to-r t-from-automotive-primary t-to-automotive-secondary hover:t-from-automotive-secondary hover:t-to-automotive-primary t-text-white t-font-semibold t-py-4 t-px-8 t-rounded-2xl t-transition-all t-duration-300 t-shadow-lg hover:t-shadow-xl hover:t-scale-[1.02] t-transform focus:t-ring-4 focus:t-ring-automotive-primary/20 focus:t-outline-none"><span>Browse Vehicles</span></Link></div> : <div className="t-grid t-grid-cols-1 md:t-grid-cols-2 lg:t-grid-cols-3 xl:t-grid-cols-4 t-gap-6">{savedVehicles.map(savedVehicle => {
        const refreshed = apiUrl ? currentVehicles[savedVehicle.vin] : undefined;
        const vehicle = refreshed?.vehicle ?? savedVehicle.vehicle;
        const showPrice = !apiUrl || refreshed?.showPrice === true;
        const availability = apiUrl ? refreshed?.availability ?? "Checking availability\u2026" : undefined;
        const vehicleName = `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""} ${vehicle.trim || ""}`.trim();
        return <div key={savedVehicle.vin} className="t-bg-white t-rounded-lg t-shadow-card t-overflow-hidden t-transition-transform hover:t-scale-[1.02] t-group"><Link href={`/cars/${savedVehicle.vin}`} onClick={() => {
            sendGTMEvent({
              event: "select_item",
              event_category: "favorites",
              event_label: "vehicle_image_click",
              item_id: savedVehicle.vin,
              item_name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
              price: vehicle.price
            });
          }}><div className="t-relative t-aspect-[4/3] t-bg-gray-100">{vehicle.image ? <Image src={vehicle.image} alt={vehicleName} fill={true} className="t-object-cover" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw" /> : <div className="t-w-full t-h-full t-flex t-items-center t-justify-center t-text-gray-400"><FontAwesomeIcon icon={faCar} className="t-text-4xl" /></div>}<div className="t-absolute t-top-3 t-right-3 t-z-30 t-w-9 t-h-9 t-rounded-full t-shadow-lg t-flex t-items-center t-justify-center t-border-2 t-bg-brand-rose t-border-brand-rose t-text-white"><Heart className="t-w-4 t-h-4 t-fill-current" /></div>{showPrice && vehicle.price && savedVehicle.originalPrice && vehicle.price !== savedVehicle.originalPrice && <div className={`t-absolute t-top-2 t-left-2 t-px-2 t-py-1 t-rounded t-text-xs t-font-semibold t-text-white ${vehicle.price < savedVehicle.originalPrice ? "t-bg-brand-green-dark" : "t-bg-brand-rose"}`}>{vehicle.price < savedVehicle.originalPrice ? "\u2193" : "\u2191"}${Math.abs(vehicle.price - savedVehicle.originalPrice).toLocaleString()}</div>}</div></Link>{availability && <p role="status" className="t-px-2 t-pt-2 t-text-sm t-font-medium">{availability}</p>}<div className="t-p-2 t-flex-1 t-flex t-flex-col"><Link href={`/cars/${savedVehicle.vin}`} onClick={() => {
              const v_0 = {
                vin: savedVehicle.vin,
                name: `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim(),
                ...(typeof vehicle.price === "number" ? {
                  price: vehicle.price
                } : {}),
                ...(vehicle.make ? {
                  make: String(vehicle.make)
                } : {}),
                ...(vehicle.model ? {
                  model: String(vehicle.model)
                } : {}),
                ...(typeof vehicle.year === "number" ? {
                  year: vehicle.year
                } : {})
              };
              sendGTMEvent({
                event: "select_item",
                item_list_name: "favorites",
                items: [toGAItem(v_0)],
                list_page: 1,
                list_sort: "favorites"
              });
            }} className="t-no-underline t-text-inherit t-cursor-pointer t-block" aria-label={`View details for ${vehicleName}`}><div className="t-mb-1"><h5 className="t-text-sm t-font-medium t-text-automotive-dark t-leading-tight t-mb-0.5 hover:t-text-brand-purple t-transition-colors" itemProp="name">{vehicle.year || ""} {vehicle.make || ""}</h5><p className="t-text-xs t-text-ecommerce-text-light t-m-0 t-mb-0.5" itemProp="model">{vehicle.model || ""} {vehicle.trim ? vehicle.trim : ""}</p></div><div className="t-flex t-items-center t-mb-1"><span className="t-text-lg t-font-bold t-text-automotive-success hover:t-text-brand-purple t-transition-colors">{showPrice ? formatPrice(vehicle.price) : "Contact for Price"}</span><span className="t-text-sm t-text-ecommerce-text-muted t-mx-2">•</span><span className="t-text-sm t-text-ecommerce-text-muted t-font-normal hover:t-text-brand-purple t-transition-colors">{formatMileage(vehicle.odometer)}</span></div></Link>{(vehicle.stockId || vehicle.dealerStockId) && <div className="t-text-xs t-text-ecommerce-text-muted t-mb-1">Stock: {vehicle.stockId || vehicle.dealerStockId}</div>}<div className="t-text-xs t-text-ecommerce-text-muted t-mt-2">Added {formatDistanceToNow(savedVehicle.savedAt, {
                addSuffix: true
              })}</div><div className="t-flex t-justify-center t-items-center t-my-3"><a href="tel:18008447227" onClick={() => {
                const v_1 = {
                  vin: savedVehicle.vin,
                  name: `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim(),
                  ...(typeof vehicle.price === "number" ? {
                    price: vehicle.price
                  } : {}),
                  ...(vehicle.make ? {
                    make: String(vehicle.make)
                  } : {}),
                  ...(vehicle.model ? {
                    model: String(vehicle.model)
                  } : {}),
                  ...(typeof vehicle.year === "number" ? {
                    year: vehicle.year
                  } : {})
                };
                sendGTMEvent({
                  event: "click_to_call",
                  ...createPhoneClickEventData("click_to_call", v_1)
                });
              }} className="t-rounded-full t-text-xs t-font-medium t-px-3 t-py-1 t-no-underline t-border t-border-ecommerce-border t-text-ecommerce-text-muted t-bg-ecommerce-background t-transition-all t-duration-200 hover:t-bg-brand-purple hover:t-text-white hover:t-border-brand-purple t-flex t-items-center t-gap-1"><Phone className="t-w-3 t-h-3" />1-800-THIScar</a></div><div className="t-flex t-gap-2 t-mt-3"><Link href={`/cars/${savedVehicle.vin}`} onClick={() => {
                const v_2 = {
                  vin: savedVehicle.vin,
                  name: `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim(),
                  ...(typeof vehicle.price === "number" ? {
                    price: vehicle.price
                  } : {}),
                  ...(vehicle.make ? {
                    make: String(vehicle.make)
                  } : {}),
                  ...(vehicle.model ? {
                    model: String(vehicle.model)
                  } : {}),
                  ...(typeof vehicle.year === "number" ? {
                    year: vehicle.year
                  } : {})
                };
                sendGTMEvent({
                  event: "select_item",
                  item_list_name: "favorites",
                  items: [toGAItem(v_2)],
                  list_page: 1,
                  list_sort: "favorites"
                });
              }} className="t-flex-1 t-bg-gradient-to-r t-from-automotive-primary t-to-automotive-secondary hover:t-from-automotive-secondary hover:t-to-automotive-primary t-text-white t-font-semibold t-text-center t-py-3 t-px-4 t-rounded-xl t-text-sm t-transition-all t-duration-300 t-shadow-md hover:t-shadow-lg hover:t-scale-[1.02] t-transform t-flex t-items-center t-justify-center t-gap-2 focus:t-ring-4 focus:t-ring-automotive-primary/20 focus:t-outline-none"><FontAwesomeIcon icon={faEye} className="t-text-xs" /><span>View Details</span></Link><button onClick={() => handleRemove(savedVehicle.vin)} aria-label={`Remove ${vehicleName} from favorites`} disabled={removingVin === savedVehicle.vin} className="t-bg-white hover:t-bg-brand-rose t-text-gray-500 hover:t-text-white t-border-2 t-border-gray-300 hover:t-border-brand-rose t-py-3 t-px-4 t-rounded-xl t-text-sm t-font-semibold t-transition-all t-duration-300 t-shadow-md hover:t-shadow-lg hover:t-scale-[1.02] t-transform disabled:t-opacity-50 disabled:t-cursor-not-allowed focus:t-ring-4 focus:t-ring-brand-rose/20 focus:t-outline-none">{removingVin === savedVehicle.vin ? "..." : <FontAwesomeIcon icon={faTrash} />}</button></div></div></div>;
      })}</div>;
    $[26] = currentVehicles;
    $[27] = formatPrice;
    $[28] = handleRemove;
    $[29] = removingVin;
    $[30] = savedVehicles;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== savedVehicles.length) {
    t14 = savedVehicles.length >= 3 && <div className="t-mt-12 t-bg-brand-yellow t-rounded-lg t-p-8 t-text-center"><h3 className="t-text-2xl t-font-semibold t-text-automotive-dark t-mb-2">Get Inventory Alerts</h3><p className="t-text-automotive-dark/80 t-mb-4">Sign up to receive notifications when similar vehicles arrive, pricing changes, and save searches</p><button className="t-bg-gradient-to-r t-from-automotive-dark t-to-gray-800 hover:t-from-automotive-primary hover:t-to-automotive-secondary t-text-white t-font-semibold t-px-8 t-py-4 t-rounded-2xl t-transition-all t-duration-300 t-shadow-lg hover:t-shadow-xl hover:t-scale-[1.02] t-transform focus:t-ring-4 focus:t-ring-automotive-primary/20 focus:t-outline-none">Coming Soon</button></div>;
    $[32] = savedVehicles.length;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t14) {
    t15 = <div className="t-bg-ecommerce-background t-py-12"><div className="t-max-w-screen-2xl t-mx-auto t-px-4">{t12}{t13}{t14}</div></div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}
function _temp6() {
  sendGTMEvent({
    event: "select_promotion",
    event_category: "favorites",
    event_label: "browse_vehicles_empty_state",
    promotion_name: "Browse Vehicles"
  });
}
function _temp5() {
  sendGTMEvent({
    event: "select_promotion",
    event_category: "favorites",
    event_label: "back_to_inventory",
    promotion_name: "Back to Inventory"
  });
}
function _temp4(_, i) {
  return <div key={i} className="t-bg-white t-rounded-lg t-overflow-hidden"><div className="t-aspect-[4/3] t-bg-ecommerce-background-alt" /><div className="t-p-4 t-space-y-2"><div className="t-h-4 t-bg-ecommerce-background-alt t-rounded t-w-3/4" /><div className="t-h-4 t-bg-ecommerce-background-alt t-rounded t-w-1/2" /></div></div>;
}
function _temp3(sv) {
  const car = sv.vehicle as any;
  const v = {
    vin: sv.vin,
    name: `${car?.year || ""} ${car?.make || ""} ${car?.model || ""}`.trim(),
    ...(typeof car?.price === "number" ? {
      price: car.price
    } : {}),
    ...(car?.make ? {
      make: String(car.make)
    } : {}),
    ...(car?.model ? {
      model: String(car.model)
    } : {}),
    ...(typeof car?.year === "number" ? {
      year: car.year
    } : {})
  };
  return toGAItem(v);
}
function _temp2(mileage) {
  if (mileage === null || !Number.isFinite(mileage) || mileage < 0) {
    return "N/A";
  }
  return `${mileage.toLocaleString()} mi`;
}
async function _temp(saved) {
  return [saved.vin, await refreshSavedVehicle(saved)] as const;
}